import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { DashboardProvider } from './DashboardContext';
import { cn } from '@/lib/utils';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from "@sentry/react";

interface DashboardLayoutProps {
    children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <DashboardProvider>
            <div className="flex h-screen bg-background overflow-hidden selection:bg-primary/20 selection:text-foreground">
                {/* Desktop Sidebar */}
                <Sidebar className="hidden lg:flex" />

                {/* Mobile Sidebar Overlay */}
                {isMobileMenuOpen && (
                    <div
                        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm lg:hidden transition-all animate-in fade-in duration-300"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}

                {/* Mobile Sidebar */}
                <div className={cn(
                    "fixed inset-y-0 left-0 z-[70] w-64 max-w-[85vw] bg-background lg:hidden transform transition-transform duration-300 ease-in-out shadow-2xl",
                    isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                )}>
                    <div className="relative w-full h-full flex flex-col">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-4 right-4 z-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label="Close menu"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                        <Sidebar className="w-full flex-1 min-h-0" onAction={() => setIsMobileMenuOpen(false)} />
                    </div>
                </div>

                {/* Main Content Area - Geist font via theme-dashboard, refined radius */}
                <main className="flex-1 h-full overflow-y-auto relative scroll-smooth bg-background font-dashboard">
                    {/* Mobile Header */}
                    <div className="lg:hidden sticky top-0 z-50 flex items-center h-16 px-6 bg-background/80 backdrop-blur-md border-b border-border">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="mr-4 -ml-2"
                        >
                            <Menu className="w-6 h-6" />
                        </Button>
                        <span className="font-bold text-foreground">ChytBot</span>
                    </div>

                    <div className="h-full">
                        <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-red-500">Something went wrong loading this page. Please try refreshing.</div>}>
                            {children}
                        </Sentry.ErrorBoundary>
                    </div>
                </main>
            </div>
        </DashboardProvider>
    );
}
